import './itemstore.css';
import { STORE_ITEMS, type StoreItemDef, type StoreItemKind } from '../store/catalog';
import { EMOTES_BY_ID } from '../emotes/catalog';
import { t } from './i18n';

/** Store tabs group the catalog's kinds into browsable categories — 'all' (the
 *  default view) plus one tab per kind that has more than a couple of items;
 *  the one-off nickname action and the not-yet-live stub cosmetics share an
 *  'other' tab rather than each getting a near-empty one of their own. */
type StoreCategory = 'all' | 'emote' | 'cueColor' | 'music' | 'other';

function categoryOf(kind: StoreItemKind): StoreCategory {
  return kind === 'nickname' || kind === 'stub' ? 'other' : kind;
}

const CATEGORY_TABS: { id: StoreCategory; labelKey: string }[] = [
  { id: 'all', labelKey: 'store.cat.all' },
  { id: 'emote', labelKey: 'store.cat.emote' },
  { id: 'cueColor', labelKey: 'store.cat.cueColor' },
  { id: 'music', labelKey: 'store.cat.music' },
  { id: 'other', labelKey: 'store.cat.other' },
];

/**
 * Item store: spend 코인 (coin) to buy emotes (unlocks them for the wheel config),
 * custom cue-ball colours (equip one once owned), curated music tracks (drop
 * straight into the room's shared playlist once owned), a nickname change
 * (a priced action, not an owned item), and stub cosmetics (owned + persisted,
 * no effect yet). Opened from the main menu, only when the server reports the
 * 'itemStore' feature gate is on.
 */
export interface ItemStorePanelCallbacks {
  onBuy(itemId: string): void;
  onEquip(itemId: string): void;
  onPlayMusic(videoId: string, title: string): void;
  onRename(name: string): void;
  onPreviewSound(sound: string): void;
  onClose(): void;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

export class ItemStorePanel {
  private readonly panel = el('div', 'hud-panel interactive');
  private readonly balance = el('div', 'store-balance');
  private readonly tabs = el('div', 'store-tabs');
  private readonly grid = el('div', 'store-grid');
  private readonly confirmOverlay = el('div', 'store-confirm-overlay');
  private readonly confirmBody = el('div', 'store-confirm-body');
  private readonly confirmBuyBtn = el('button', 'review-action store-buy');
  private coins = 0;
  private owned = new Set<string>();
  private equipped: string | null = null;
  private category: StoreCategory = 'all';

  constructor(root: HTMLElement, private cb: ItemStorePanelCallbacks) {
    this.panel.id = 'store-panel';
    this.panel.style.display = 'none';

    const header = el('div', 'review-header');
    const heads = el('div', 'review-heads');
    const title = el('div', 'review-title');
    title.textContent = t('store.title');
    heads.append(title, this.balance);
    const close = el('button', 'review-close');
    close.textContent = t('mainmenu.back');
    close.onclick = () => {
      this.hide();
      this.cb.onClose();
    };
    header.append(heads, close);

    for (const tab of CATEGORY_TABS) {
      const btn = el('button', 'store-tab');
      btn.textContent = t(tab.labelKey);
      btn.classList.toggle('active', tab.id === this.category);
      btn.onclick = () => {
        this.category = tab.id;
        for (const child of Array.from(this.tabs.children)) child.classList.toggle('active', child === btn);
        this.render();
      };
      this.tabs.appendChild(btn);
    }

    this.panel.append(header, this.tabs, this.grid);
    root.appendChild(this.panel);

    // --- purchase confirmation dialog: current balance + balance after ------
    const confirmPanel = el('div', 'store-confirm-panel interactive');
    const confirmTitle = el('div', 'store-confirm-title');
    confirmTitle.textContent = t('store.confirmTitle');
    const confirmFooter = el('div', 'store-confirm-footer');
    const confirmCancel = el('button', 'review-action');
    confirmCancel.textContent = t('store.confirmCancel');
    confirmCancel.onclick = () => this.hideConfirm();
    confirmFooter.append(confirmCancel, this.confirmBuyBtn);
    confirmPanel.append(confirmTitle, this.confirmBody, confirmFooter);
    this.confirmOverlay.appendChild(confirmPanel);
    this.confirmOverlay.style.display = 'none';
    this.confirmOverlay.addEventListener('pointerdown', (e) => {
      if (e.target === this.confirmOverlay) this.hideConfirm();
    });
    this.panel.appendChild(this.confirmOverlay);
  }

  private hideConfirm(): void {
    this.confirmOverlay.style.display = 'none';
  }

  /** Show a confirm dialog for a priced purchase; runs onConfirm if the user goes through. */
  private confirmPurchase(item: StoreItemDef, onConfirm: () => void): void {
    const after = this.coins - item.price;
    this.confirmBody.replaceChildren();
    const label = el('div', 'store-confirm-item');
    label.textContent = item.label;
    const current = el('div', 'store-confirm-line');
    current.textContent = t('store.confirmCurrent', { n: this.coins });
    const post = el('div', 'store-confirm-line');
    post.textContent = t('store.confirmAfter', { n: after });
    this.confirmBody.append(label, current, post);
    this.confirmBuyBtn.textContent = t('store.buy', { n: item.price });
    this.confirmBuyBtn.onclick = () => {
      this.hideConfirm();
      onConfirm();
    };
    this.confirmOverlay.style.display = 'flex';
  }

  get isOpen(): boolean {
    return this.panel.style.display !== 'none';
  }

  /** Show the panel with a coin balance + owned-items snapshot. */
  open(coins: number, owned: string[], equippedCueColor: string | null = null): void {
    this.setState(coins, owned, equippedCueColor);
    this.panel.style.display = 'flex';
  }

  /** Refresh balance/ownership/equipped-colour in place (e.g. after a purchase). */
  setState(coins: number, owned: string[], equippedCueColor: string | null = null): void {
    this.coins = coins;
    this.owned = new Set(owned);
    this.equipped = equippedCueColor
      ? (STORE_ITEMS.find((i) => i.kind === 'cueColor' && i.hex === equippedCueColor)?.id ?? null)
      : null;
    this.balance.textContent = t('store.balance', { n: this.coins });
    this.render();
  }

  hide(): void {
    this.panel.style.display = 'none';
  }

  private render(): void {
    this.grid.replaceChildren();
    for (const item of STORE_ITEMS) {
      if (this.category !== 'all' && categoryOf(item.kind) !== this.category) continue;
      const card = el('div', 'store-card');
      const owns = this.owned.has(item.id);
      if (!owns && this.coins < item.price) card.classList.add('unaffordable');
      const glyph = el('div', 'store-glyph');
      if (item.kind === 'cueColor' && item.hex) {
        glyph.classList.add('store-swatch');
        glyph.style.background = '#' + item.hex;
      } else {
        glyph.textContent = item.glyph;
      }
      // Sound-backed emotes get a tap-to-preview right on the card — you can
      // hear a voice line before spending coins on it, not just see its glyph.
      const emoteDef = item.kind === 'emote' ? EMOTES_BY_ID.get(item.id) : undefined;
      if (emoteDef?.kind === 'sound' && emoteDef.sound) {
        const sound = emoteDef.sound;
        const preview = el('button', 'store-preview');
        preview.textContent = '▶';
        preview.title = t('store.preview');
        preview.onclick = (e) => {
          e.stopPropagation();
          this.cb.onPreviewSound(sound);
        };
        glyph.appendChild(preview);
      }
      const label = el('div', 'store-label');
      label.textContent = item.label;
      card.append(glyph, label);

      if (item.kind === 'stub') {
        const soon = el('div', 'store-soon');
        soon.textContent = t('store.comingSoon');
        card.append(soon);
      }

      if (item.kind === 'nickname') {
        const btn = el('button', 'review-action store-buy');
        btn.textContent = t('store.buy', { n: item.price });
        btn.disabled = this.coins < item.price;
        btn.onclick = () => {
          const name = window.prompt(t('store.renamePrompt'));
          if (name && name.trim()) this.confirmPurchase(item, () => this.cb.onRename(name.trim()));
        };
        card.append(btn);
        this.grid.appendChild(card);
        continue;
      }

      if (owns) {
        card.classList.add('owned');
        if (item.kind === 'cueColor') {
          const isEquipped = this.equipped === item.id;
          const btn = el('button', 'review-action store-buy');
          btn.textContent = t(isEquipped ? 'store.equipped' : 'store.equip');
          btn.disabled = isEquipped;
          btn.onclick = () => this.cb.onEquip(item.id);
          card.append(btn);
        } else if (item.kind === 'music') {
          const btn = el('button', 'review-action store-buy');
          btn.textContent = t('store.play');
          btn.onclick = () => this.cb.onPlayMusic(item.videoId!, item.label);
          card.append(btn);
        } else {
          const owned = el('button', 'review-action store-buy');
          owned.textContent = t('store.owned');
          owned.disabled = true;
          card.append(owned);
        }
      } else {
        const btn = el('button', 'review-action store-buy');
        btn.textContent = t('store.buy', { n: item.price });
        btn.disabled = this.coins < item.price;
        btn.onclick = () => this.confirmPurchase(item, () => this.cb.onBuy(item.id));
        card.append(btn);
      }
      this.grid.appendChild(card);
    }
  }
}
